import { Navigate, Outlet, Route, Routes } from "react-router-dom";
import type { ReactNode } from "react";

import LoginPage from "./app/login/page";
import SignupPage from "./app/signup/page";
import MainLayout from "./app/main/layout";
import DashboardPage from "./app/main/page";
import AccountingPage from "./app/main/accounting/page";
import AssignPage from "./app/main/assign/page";
import ClassesPage from "./app/main/classes/page";
import EmployeePage from "./app/main/employee/page";
import MembersPage from "./app/main/members/page";
import PackageAnalyticsPage from "./app/main/packageAnalytics/page";
import PackagesPage from "./app/main/packages/page";
import SalesPage from "./app/main/sales/page";
import SalesReportsPage from "./app/main/salesReports/page";
import SalaryPage from "./app/main/salary/page";
import SessionsPage from "./app/main/sessions/page";
import SettingsPage from "./app/main/settings/page";
import StockPage from "./app/main/stock/page";
import SubscriptionsPage from "./app/main/subscriptions/page";
import AllSubscriptionsPage from "./app/main/subscriptions/all/page";
import PrivateSubscriptionsPage from "./app/main/privatesubscriptions/page";
import UsersPage from "./app/main/users/page";
import { useAuth } from "./app/context/AuthContext";
import { hasPermission } from "./app/utils/permissions";
import { FEATURE_KEYS, FeatureGate, FeatureKey } from "./app/utils/featureAccess";

const ProtectedRoutes = () => (
  <MainLayout>
    <Outlet />
  </MainLayout>
);

const PermissionRoute = ({ permission, children }: { permission: string; children: ReactNode }) => {
  const { user } = useAuth();
  return hasPermission(user, permission) ? <>{children}</> : <Navigate to="/" replace />;
};

const AnyPermissionRoute = ({ permissions, children }: { permissions: string[]; children: ReactNode }) => {
  const { user } = useAuth();
  return permissions.some((permission) => hasPermission(user, permission)) ? <>{children}</> : <Navigate to="/" replace />;
};

const FeaturePermissionRoute = ({
  feature,
  permission,
  children,
}: {
  feature: FeatureKey;
  permission: string;
  children: ReactNode;
}) => (
  <FeatureGate feature={feature}>
    <PermissionRoute permission={permission}>{children}</PermissionRoute>
  </FeatureGate>
);

const FeatureAnyPermissionRoute = ({
  feature,
  permissions,
  children,
}: {
  feature: FeatureKey;
  permissions: string[];
  children: ReactNode;
}) => (
  <FeatureGate feature={feature}>
    <AnyPermissionRoute permissions={permissions}>{children}</AnyPermissionRoute>
  </FeatureGate>
);

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/signup" element={<SignupPage />} />

      <Route element={<ProtectedRoutes />}>
        <Route index element={<PermissionRoute permission="dashboard.view"><DashboardPage /></PermissionRoute>} />
        <Route path="main" element={<Navigate to="/" replace />} />
        <Route path="accounting" element={<FeaturePermissionRoute feature={FEATURE_KEYS.ACCOUNTING} permission="accounting.view"><AccountingPage /></FeaturePermissionRoute>} />
        <Route path="assign" element={<FeaturePermissionRoute feature={FEATURE_KEYS.PRIVATE_TRAINING} permission="assign.view"><AssignPage /></FeaturePermissionRoute>} />
        <Route path="classes" element={<FeaturePermissionRoute feature={FEATURE_KEYS.MOBILE_APP} permission="classes.view"><ClassesPage /></FeaturePermissionRoute>} />
        <Route path="employee" element={<FeaturePermissionRoute feature={FEATURE_KEYS.PRIVATE_TRAINING} permission="employees.view"><EmployeePage /></FeaturePermissionRoute>} />
        <Route path="members" element={<FeaturePermissionRoute feature={FEATURE_KEYS.MEMBERS_SUBSCRIPTIONS} permission="members.view"><MembersPage /></FeaturePermissionRoute>} />
        <Route path="package-analytics" element={<FeaturePermissionRoute feature={FEATURE_KEYS.MEMBERS_SUBSCRIPTIONS} permission="packages.view"><PackageAnalyticsPage /></FeaturePermissionRoute>} />
        <Route path="packages" element={<FeaturePermissionRoute feature={FEATURE_KEYS.MEMBERS_SUBSCRIPTIONS} permission="packages.view"><PackagesPage /></FeaturePermissionRoute>} />
        <Route path="sales" element={<FeaturePermissionRoute feature={FEATURE_KEYS.STOCK_PRODUCTS} permission="sales.view"><SalesPage /></FeaturePermissionRoute>} />
        <Route path="salesReports" element={<FeaturePermissionRoute feature={FEATURE_KEYS.ANALYTICS} permission="sales_reports.view"><SalesReportsPage /></FeaturePermissionRoute>} />
        <Route path="salary" element={<FeaturePermissionRoute feature={FEATURE_KEYS.PRIVATE_TRAINING} permission="salary.view"><SalaryPage /></FeaturePermissionRoute>} />
        <Route path="sessions" element={<FeaturePermissionRoute feature={FEATURE_KEYS.PRIVATE_TRAINING} permission="sessions.view"><SessionsPage /></FeaturePermissionRoute>} />
        <Route path="settings" element={<AnyPermissionRoute permissions={["settings.view"]}><SettingsPage /></AnyPermissionRoute>} />
        <Route path="stock" element={<FeaturePermissionRoute feature={FEATURE_KEYS.STOCK_PRODUCTS} permission="stock.view"><StockPage /></FeaturePermissionRoute>} />
        <Route path="subscriptions" element={<FeaturePermissionRoute feature={FEATURE_KEYS.MEMBERS_SUBSCRIPTIONS} permission="subscriptions.view"><SubscriptionsPage /></FeaturePermissionRoute>} />
        <Route path="subscriptions/all" element={<FeaturePermissionRoute feature={FEATURE_KEYS.MEMBERS_SUBSCRIPTIONS} permission="subscriptions.view"><AllSubscriptionsPage /></FeaturePermissionRoute>} />
        <Route path="privatesubscriptions" element={<FeaturePermissionRoute feature={FEATURE_KEYS.PRIVATE_TRAINING} permission="subscriptions.view"><PrivateSubscriptionsPage /></FeaturePermissionRoute>} />
        <Route path="roles" element={<Navigate to="/users" replace />} />
        <Route path="users" element={<AnyPermissionRoute permissions={["users.view", "settings.view"]}><UsersPage /></AnyPermissionRoute>} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
